"use client"

import { useState, useEffect, useRef } from "react"
import axiosInstance from "../../../utils/axiosInstance"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Printer, Calendar, Clock } from 'lucide-react'
import FeePayment from "./StudentFeePayment"
import StudentPaymentHistory from "./StudentPaymentHistory"
import { API_ENDPOINTS } from "../../../constants/config"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"

function FeeManagement() {
  const [quarterlyFees, setQuarterlyFees] = useState([])
  const [paymentHistory, setPaymentHistory] = useState([])
  const [error, setError] = useState("")
  const [student, setStudent] = useState(null)
  const [isReceiptDialogOpen, setIsReceiptDialogOpen] = useState(false)
  const [selectedFee, setSelectedFee] = useState(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [selectedQuarterFee, setSelectedQuarterFee] = useState(null)
  const [loading, setLoading] = useState(true)
  const [initialLoading, setInitialLoading] = useState(true)
  const [schoolDetails, setSchoolDetails] = useState(null)
  const receiptRef = useRef(null)
  const hiddenReceiptRef = useRef(null)

  useEffect(() => {
    const initData = async () => {
      setInitialLoading(true)
      await Promise.all([fetchStudentData(), fetchSchoolDetails()])
      setInitialLoading(false)
    }
    initData()
  }, [])

  useEffect(() => {
    if (student?._id) {
      const fetchAllData = async () => {
        setLoading(true)
        try {
          await Promise.all([fetchQuarterlyFees(), fetchPaymentHistory()])
        } catch (error) {
          console.error("Error fetching data:", error)
        } finally {
          setLoading(false)
        }
      }
      fetchAllData()
    }
  }, [student?._id])

  const fetchStudentData = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.usersProfile, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setStudent(response.data.data.user)
    } catch (error) {
      console.error("Error fetching student data:", error)
      setError("Failed to fetch student data: " + (error.response?.data?.message || error.message))
    }
  }

  const fetchSchoolDetails = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.schoolAbout, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.data.status === "success") {
        setSchoolDetails(response.data.data.school)
      }
    } catch (error) {
      console.error("Error fetching school details:", error)
    }
  }

  const fetchLateFee = async (feeId) => {
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.feesLateFee(feeId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data.data
    } catch (error) {
      // Only log error if it's not a 404 (fee schedule not found)
      if (error.response?.status !== 404) {
        console.error(`Error fetching late fee for ${feeId}:`, error)
        console.error("Late fee API error details:", error.response?.data)
      }
      return null
    }
  }

  const fetchQuarterlyFees = async () => {
    setLoading(true)
    setError("")
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.feesStudentQuarterly(student._id), {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.data.status === "success" && response.data.data.quarters) {
        const quarters = response.data.data.quarters

        // Try to get detailed student fee records for enrichment (late fee/platform fee).
        // If this endpoint returns 404 for current year/enrollment edge-cases, continue with quarterly data.
        let studentFees = []
        try {
          const studentFeesResponse = await axiosInstance.get(
            API_ENDPOINTS.feesStudentFees(student._id),
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          studentFees = studentFeesResponse.data?.data?.fees || []
        } catch (studentFeesError) {
          if (studentFeesError?.response?.status !== 404) {
            throw studentFeesError
          }
          console.warn(
            "Detailed student fee records not found; continuing with quarterly fee data.",
            studentFeesError?.response?.data || studentFeesError?.message,
          )
        }

        const formattedFees = await Promise.all(
          Object.entries(quarters).map(async ([quarterKey, data]) => {
            // Find the corresponding fee record by quarter
            const matchingFee = studentFees.find((fee) => fee.quarter?.toLowerCase() === data.quarter?.toLowerCase())

            let lateFeeDetails = matchingFee?.lateFeeDetails ?? null
            if (matchingFee?._id && !lateFeeDetails) {
              lateFeeDetails = await fetchLateFee(matchingFee._id)
            }

            const lateFeeAmount = lateFeeDetails?.lateFeeAmount ?? 0
            const totalWithLateFee = data.totalAmount + lateFeeAmount
            const platformFee = matchingFee?.platformFee ?? 0
            const totalPayable = matchingFee?.totalPayable ?? (totalWithLateFee + platformFee)

            return {
              _id: matchingFee?._id || `${quarterKey}-${Date.now()}`,
              amount: totalPayable,
              dueDate: data.dueDate,
              isLive: data.isLive,
              isPaid: data.isPaid,
              status: data.status,
              paymentDate: data.paymentDate,
              quarter: data.quarter,
              totalAmount: matchingFee?.totalAmount || data.totalAmount, // Use actual database totalAmount for paid fees
              originalAmount: data.totalAmount,
              busFees: data.busFees,
              lateFees: data.lateFees,
              scholarshipFees: data.scholarshipFees,
              paidDate: data.paidDate,
              feeBreakup: matchingFee?.feeBreakup ?? data.feeBreakup ?? [],
              lateFeeDetails: lateFeeDetails,
              platformFee,
              totalPayable,
              makeLiveDate: data.makeLiveDate || matchingFee?.makeLiveDate,
            }
          }),
        )

        setQuarterlyFees(formattedFees)
      } else {
        setQuarterlyFees([])
      }
    } catch (error) {
      console.error("Error fetching quarterly fees:", error)
      setError("Failed to fetch quarterly fees: " + (error.response?.data?.message || error.message))
      setQuarterlyFees([])
    } finally {
      setLoading(false)
    }
  }

  const fetchPaymentHistory = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.feesStudentHistory, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.data.status === "success" && response.data.data && response.data.data.payments) {
        setPaymentHistory(response.data.data.payments)
      } else {
        setPaymentHistory([])
      }
    } catch (error) {
      console.error("Error fetching payment history:", error)
      setError("Failed to fetch payment history: " + (error.response?.data?.message || error.message))
      setPaymentHistory([])
    }
  }

  const handlePrintReceipt = async (feeOrPayment) => {
    const hasBreakup = feeOrPayment?.feeBreakup?.length > 0
    const fullFee = hasBreakup ? feeOrPayment : (quarterlyFees.find((f) => f._id === feeOrPayment?._id) || feeOrPayment)
    await generatePDFFromFee(fullFee)
  }

  const generatePDFFromFee = async (fee) => {
    if (!fee || !schoolDetails) return

    const baseFromBreakup = fee.feeBreakup?.reduce((s, b) => s + (b.amount || 0), 0) ?? 0
    const lateFeeAmount = fee.lateFeeDetails?.lateFeeAmount ?? fee.lateFees ?? 0
    const receiptTotal = fee.totalPayable ?? (baseFromBreakup + lateFeeAmount)

    try {
      // Create a temporary container for the receipt
      const tempDiv = document.createElement("div")
      tempDiv.style.position = "absolute"
      tempDiv.style.left = "-9999px"
      tempDiv.style.width = "600px"
      tempDiv.style.backgroundColor = schoolDetails?.receiptOuterColor || '#ffffff'
      tempDiv.style.padding = "0"
      tempDiv.style.margin = "0"
      document.body.appendChild(tempDiv)

      // Create main container
      const mainContainer = document.createElement("div")
      mainContainer.style.cssText = `background-color: ${schoolDetails?.receiptOuterColor || '#ffffff'}; width: 600px; display: inline-block; position: relative; min-height: 400px;`
      
      // Create content container
      const contentContainer = document.createElement("div")
      contentContainer.style.cssText = "position: relative; z-index: 1;"
      
      // Render receipt content
      contentContainer.innerHTML = `
            <div style="text-align: center; padding-bottom: 0.75rem; padding-top: 0.75rem; background-color: ${schoolDetails?.receiptOuterColor || '#ffffff'};">
              <div style="padding-left: 1.5rem; padding-right: 1.5rem;">
                <h3 style="font-size: 1rem; font-weight: bold; margin: 0;">${schoolDetails?.name || "School Name"}</h3>
                <p style="font-size: 0.75rem; color: #6b7280; margin: 0.25rem 0;">
                  ${schoolDetails?.address?.street ? `${schoolDetails.address.street}, ` : ''}
                  ${schoolDetails?.address?.city ? `${schoolDetails.address.city}, ` : ''}
                  ${schoolDetails?.address?.state ? `${schoolDetails.address.state} - ` : ''}
                  ${schoolDetails?.address?.pincode || ''}
                </p>
                <p style="font-size: 0.75rem; color: #6b7280; margin: 0.25rem 0;">
                  ${schoolDetails?.phone ? `Phone: ${schoolDetails.phone}` : ''}
                  ${schoolDetails?.email ? ` | Email: ${schoolDetails.email}` : ''}
                </p>
              </div>
            </div>
            <div style="padding: 0.75rem 1.5rem; margin-left: 1.5rem; margin-right: 1.5rem; background-color: ${schoolDetails?.receiptInnerColor || '#fef3e2'};">
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; padding-bottom: 0.75rem;">
                <div>
                  <p style="font-size: 0.75rem; font-weight: 500; margin: 0;">Receipt No:</p>
                  <p style="font-size: 0.75rem; margin: 0;">REC-${fee._id ? fee._id.substring(0, 8) : "N/A"}</p>
                </div>
                <div>
                  <p style="font-size: 0.75rem; font-weight: 500; margin: 0;">Receipt Date:</p>
                  <p style="font-size: 0.75rem; margin: 0;">${fee.paidDate ? formatDate(fee.paidDate) : formatDate(new Date())}</p>
                </div>
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; padding-bottom: 0.75rem;">
                ${student?.admissionNumber ? `
                  <div>
                    <p style="font-size: 0.75rem; font-weight: 500; margin: 0;">Admission Number:</p>
                    <p style="font-size: 0.75rem; margin: 0;">${student.admissionNumber}</p>
                  </div>
                ` : '<div></div>'}
                <div>
                  <p style="font-size: 0.75rem; font-weight: 500; margin: 0;">Quarter:</p>
                  <p style="font-size: 0.75rem; margin: 0;">${fee.quarter?.toUpperCase() || ''}</p>
                </div>
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; padding-bottom: 0.75rem;">
                <div>
                  <p style="font-size: 0.75rem; font-weight: 500; margin: 0;">Student Name:</p>
                  <p style="font-size: 0.75rem; margin: 0;">${getFullName(student) || "Student Name"}</p>
                </div>
                ${student?.fatherName ? `
                  <div>
                    <p style="font-size: 0.75rem; font-weight: 500; margin: 0;">Father's Name:</p>
                    <p style="font-size: 0.75rem; margin: 0;">${student.fatherName}</p>
                  </div>
                ` : '<div></div>'}
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; padding-bottom: 0.75rem;">
                ${student?.enrollment?.class?.name ? `
                  <div>
                    <p style="font-size: 0.75rem; font-weight: 500; margin: 0;">Class:</p>
                    <p style="font-size: 0.75rem; margin: 0;">${student.enrollment.class.name}</p>
                  </div>
                ` : '<div></div>'}
                ${student?.enrollment?.section?.name ? `
                  <div>
                    <p style="font-size: 0.75rem; font-weight: 500; margin: 0;">Section:</p>
                    <p style="font-size: 0.75rem; margin: 0;">${student.enrollment.section.name}</p>
                  </div>
                ` : '<div></div>'}
              </div>
              <div style="border-top: 1px solid black; margin-top: 0.75rem; margin-bottom: 0.75rem;"></div>
              <div style="padding-bottom: 0.75rem;">
                <h4 style="font-size: 0.875rem; font-weight: 500; margin-bottom: 0.5rem;">Fee Breakdown:</h4>
                <table style="width: 100%; border-collapse: collapse; font-size: 0.75rem; border: 1px solid black;">
                  <thead>
                    <tr style="border-bottom: 1px solid black;">
                      <th style="text-align: left; padding: 0.25rem 0.5rem; font-weight: 500; color: #4b5563; border-right: 1px solid black;">Fee Name</th>
                      <th style="text-align: right; padding: 0.25rem 0.5rem; font-weight: 500; color: #4b5563;">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${fee.feeBreakup?.map((item) => `
                      <tr style="border-bottom: 1px solid black;">
                        <td style="padding: 0.25rem 0.5rem; color: #4b5563; border-right: 1px solid black;">${item.feeName}</td>
                        <td style="text-align: right; padding: 0.25rem 0.5rem; font-weight: 500; ${item.amount < 0 ? 'color: #16a34a;' : ''}">
                          ${item.amount < 0 ? '-' : ''}₹${Math.abs(item.amount || 0).toLocaleString()}
                        </td>
                      </tr>
                    `).join('') || ''}
                    ${lateFeeAmount > 0 && !fee.feeBreakup?.some(f => f.feeName === "Late Fees") ? `
                      <tr style="border-bottom: 1px solid black;">
                        <td style="padding: 0.25rem 0.5rem; color: #4b5563; border-right: 1px solid black;">Late Fee</td>
                        <td style="text-align: right; padding: 0.25rem 0.5rem; font-weight: 500;">₹${Math.abs(lateFeeAmount).toLocaleString()}</td>
                      </tr>
                    ` : ''}
                    ${fee.scholarshipFees > 0 && !fee.feeBreakup?.some(f => f.feeName === "Scholarship Discount") ? `
                      <tr style="border-bottom: 1px solid black;">
                        <td style="padding: 0.25rem 0.5rem; color: #4b5563; border-right: 1px solid black;">Scholarship Discount</td>
                        <td style="text-align: right; padding: 0.25rem 0.5rem; font-weight: 500; color: #16a34a;">
                          -₹${Math.abs(fee.scholarshipFees).toLocaleString()}
                        </td>
                      </tr>
                    ` : ''}
                    ${fee.platformFee > 0 && !fee.feeBreakup?.some(f => f.feeName?.includes("Platform Fee")) ? `
                      <tr style="border-bottom: 1px solid black;">
                        <td style="padding: 0.25rem 0.5rem; color: #4b5563; border-right: 1px solid black;">Platform Fee (2% + 18% GST)</td>
                        <td style="text-align: right; padding: 0.25rem 0.5rem; font-weight: 500;">₹${Math.abs(fee.platformFee).toLocaleString()}</td>
                      </tr>
                    ` : ''}
                    <tr style="border-top: 1px solid black;">
                      <td style="padding: 0.5rem 0.5rem; font-weight: 600; color: #1f2937; border-right: 1px solid black;">Total Amount Paid</td>
                      <td style="text-align: right; padding: 0.5rem 0.5rem; font-weight: bold; font-size: 0.875rem; color: #2563eb;">
                        ₹${Math.abs(receiptTotal).toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; padding-top: 0.75rem; padding-bottom: 0.75rem;">
                <div style="flex: 1;">
                  <p style="font-size: 0.75rem; font-weight: 500; margin: 0;">Payment Method:</p>
                  <p style="font-size: 0.75rem; margin: 0;">${fee.paymentMethod || "Online Payment"}</p>
                </div>
              </div>
            </div>
            <div style="display: flex; justify-content: flex-end; align-items: flex-start; padding-top: 0.75rem; padding-bottom: 0.75rem; background-color: ${schoolDetails?.receiptOuterColor || '#ffffff'};">
              <div style="text-align: right; padding-left: 1.5rem; padding-right: 1.5rem;">
                <p style="font-size: 0.75rem; font-weight: 500; margin-bottom: 0.5rem;">Authorized Signature:</p>
                ${schoolDetails?.receiptSignature ? `
                  <div style="margin-top: 0.5rem; display: flex; justify-content: flex-end;">
                    <img src="${schoolDetails.receiptSignature}" alt="Authorized Signature" style="height: 80px; width: auto; object-fit: contain; max-width: 180px; display: block;" />
                  </div>
                ` : `
                  <p style="font-size: 0.75rem; font-style: italic; margin-top: 1rem;">Principal</p>
                `}
              </div>
            </div>
          </div>
        </div>
      `
      
      // Append content container to main container
      mainContainer.appendChild(contentContainer)
      
      // Add stamp watermark AFTER content (so it's on top) - simple approach
      if (schoolDetails?.receiptStamp) {
        const stampHtml = `
          <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); pointer-events: none; z-index: 10; width: 128px; height: 128px;">
            <img src="${schoolDetails.receiptStamp}" alt="Receipt Stamp" style="width: 100%; height: 100%; object-fit: cover; border-radius: 50%; border: 2px solid rgba(209, 213, 219, 0.3); display: block; opacity: 0.15;" />
          </div>
        `
        mainContainer.innerHTML += stampHtml
      }
      
      // Append main container to temp div
      tempDiv.appendChild(mainContainer)
      
      // Verify content was added
      if (tempDiv.children.length === 0 || mainContainer.children.length === 0) {
        throw new Error('Failed to create receipt content structure')
      }
      
      console.log('Receipt structure created:', {
        tempDivChildren: tempDiv.children.length,
        mainContainerChildren: mainContainer.children.length,
        contentContainerChildren: contentContainer.children.length
      })
      
      // Force a reflow to ensure DOM is updated
      void tempDiv.offsetHeight

      // Wait for images to load properly
      const images = tempDiv.querySelectorAll('img')
      const imagePromises = Array.from(images).map((img) => {
        return new Promise((resolve) => {
          // Set crossOrigin before checking if loaded
          img.crossOrigin = 'anonymous'
          
          if (img.complete && img.naturalHeight !== 0) {
            resolve()
          } else {
            const loadHandler = () => {
              img.removeEventListener('load', loadHandler)
              img.removeEventListener('error', errorHandler)
              resolve()
            }
            const errorHandler = () => {
              img.removeEventListener('load', loadHandler)
              img.removeEventListener('error', errorHandler)
              console.warn('Image failed to load:', img.src)
              resolve() // Continue even if image fails to load
            }
            img.addEventListener('load', loadHandler)
            img.addEventListener('error', errorHandler)
            // Force reload if src is set
            if (img.src) {
              const src = img.src
              img.src = ''
              img.src = src
            }
            // Timeout after 5 seconds
            setTimeout(() => {
              img.removeEventListener('load', loadHandler)
              img.removeEventListener('error', errorHandler)
              resolve()
            }, 5000)
          }
        })
      })
      
      await Promise.all(imagePromises)
      
      // Check if stamp is visible
      const stampImg = tempDiv.querySelector('img[alt="Receipt Stamp"]')
      if (stampImg) {
        console.log('Stamp image found:', {
          src: stampImg.src,
          complete: stampImg.complete,
          naturalWidth: stampImg.naturalWidth,
          naturalHeight: stampImg.naturalHeight,
          offsetWidth: stampImg.offsetWidth,
          offsetHeight: stampImg.offsetHeight,
          style: window.getComputedStyle(stampImg).opacity
        })
      }
      
      // Additional wait for rendering
      await new Promise((resolve) => setTimeout(resolve, 1000))
      
      // Force layout recalculation and remove extra height
      tempDiv.style.display = "block"
      const actualHeight = tempDiv.scrollHeight
      tempDiv.style.height = `${actualHeight}px`
      tempDiv.style.overflow = "visible"

      // Verify tempDiv has content before capturing
      if (!tempDiv) {
        throw new Error('Receipt container not found')
      }
      
      if (tempDiv.children.length === 0) {
        console.error('tempDiv has no children:', tempDiv)
        throw new Error('Receipt content is empty. Please ensure all data is loaded.')
      }
      
      console.log('tempDiv ready for capture:', {
        children: tempDiv.children.length,
        innerHTML: tempDiv.innerHTML.substring(0, 100)
      })
      
      console.log('Capturing canvas, tempDiv height:', actualHeight, 'width: 600')
      
      let canvas
      try {
        canvas = await html2canvas(tempDiv, {
          scale: 2,
          useCORS: true,
          allowTaint: true, // Allow tainted canvas for external images
          logging: false, // Disable verbose logging
          backgroundColor: "#ffffff",
          height: actualHeight,
          width: 600,
          imageTimeout: 15000, // Increase timeout for images
          removeContainer: false,
          foreignObjectRendering: false, // Disable foreign object rendering which can cause issues
          proxy: undefined, // Don't use proxy
          onclone: (clonedDoc) => {
            // Remove crossOrigin from images in cloned doc to avoid CORS issues
            const clonedImages = clonedDoc.querySelectorAll('img')
            clonedImages.forEach((img) => {
              // Don't modify images in cloned doc, let html2canvas handle it
            })
          }
        })
      } catch (canvasError) {
        console.error('html2canvas error:', canvasError)
        throw new Error('Failed to capture receipt. Please try again.')
      }
      
      // Verify canvas was created successfully
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        console.error('Canvas is invalid:', {
          exists: !!canvas,
          width: canvas?.width,
          height: canvas?.height
        })
        throw new Error('Canvas capture failed. The receipt content may not be visible.')
      }
      
      console.log('Canvas created successfully:', canvas.width, 'x', canvas.height)

      // Convert canvas to image data with error handling
      let imgData
      let imageFormat = "PNG"
      try {
        // Try PNG first
        imgData = canvas.toDataURL("image/png")
        console.log('PNG data length:', imgData?.length)
        if (!imgData || imgData === 'data:,') {
          throw new Error('PNG conversion returned empty data')
        }
      } catch (pngError) {
        console.warn('PNG conversion failed, trying JPEG:', pngError)
        try {
          // Fallback to JPEG
          imgData = canvas.toDataURL("image/jpeg", 0.95)
          imageFormat = "JPEG"
          console.log('JPEG data length:', imgData?.length)
          if (!imgData || imgData === 'data:,') {
            throw new Error('JPEG conversion also returned empty data')
          }
        } catch (jpegError) {
          console.error('Both PNG and JPEG conversion failed:', jpegError)
          console.error('Canvas state:', {
            width: canvas.width,
            height: canvas.height,
            isEmpty: canvas.width === 0 || canvas.height === 0
          })
          throw new Error('Failed to convert canvas to image. The receipt may contain images that cannot be exported.')
        }
      }
      
      const imgWidth = 210 // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      
      // Use custom page height based on content, but max A4 height
      const pageHeight = Math.min(imgHeight, 297) // A4 max height
      const pdf = new jsPDF("p", "mm", [imgWidth, pageHeight])
      
      pdf.addImage(imgData, imageFormat, 0, 0, imgWidth, imgHeight)
      
      // If content is taller than A4, add additional pages
      if (imgHeight > 297) {
        let heightLeft = imgHeight - 297
        let position = -297
        
        while (heightLeft > 0) {
          pdf.addPage([imgWidth, Math.min(heightLeft, 297)])
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
          heightLeft -= 297
          position -= 297
        }
      }

      const fileName = `Receipt-${fee.quarter}-${fee._id?.substring(0, 8) || "REC"}.pdf`
      pdf.save(fileName)

      // Clean up
      document.body.removeChild(tempDiv)
    } catch (error) {
      console.error("Error generating PDF:", error)
      alert("Failed to generate PDF. Please try again.")
    }
  }

  const getFullName = (student) => {
    if (!student) return ""
    return [student.firstName, student.middleName, student.lastName].filter(Boolean).join(" ")
  }

  const handlePaymentComplete = () => {
    setShowPaymentForm(false)
    // Add a small delay to ensure backend has processed the payment
    setTimeout(() => {
      fetchQuarterlyFees()
      fetchPaymentHistory()
    }, 1000)
  }

  const handlePayQuarterlyFee = (fee) => {
    setSelectedQuarterFee(fee)
    setShowPaymentForm(true)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount)
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  const generatePDF = async () => {
    if (!receiptRef.current || !selectedFee) return

    try {
      const element = receiptRef.current
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: null,
      })

      const imgData = canvas.toDataURL("image/png")
      const pdf = new jsPDF("p", "mm", "a4")
      const imgWidth = 210 // A4 width in mm
      const pageHeight = 297 // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight

      let position = 0

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }

      const fileName = `Receipt-${selectedFee.quarter}-${selectedFee._id?.substring(0, 8) || "REC"}.pdf`
      pdf.save(fileName)
    } catch (error) {
      console.error("Error generating PDF:", error)
      alert("Failed to generate PDF. Please try again.")
    }
  }

  const calculateDisplayTotal = (fee) => {
    if (fee?.totalPayable != null) return fee.totalPayable;
    if (fee?.isPaid && fee?.totalAmount) return fee.totalAmount;
    return fee?.totalAmount ?? 0;
  };

  const calculateBaseTotal = (fee) => {
    const baseFromBreakup = Array.isArray(fee?.feeBreakup)
      ? fee.feeBreakup.reduce((sum, item) => sum + Math.abs(item.amount || 0), 0)
      : 0;
    const lateFee = fee?.lateFeeDetails?.lateFeeAmount ?? 0;
    const base = baseFromBreakup + lateFee;
    if (base > 0) return base;
    return fee?.totalAmount ?? (fee?.totalPayable != null ? fee.totalPayable - (fee?.platformFee ?? 0) : 0);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {error && <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-r-lg">{error}</div>}

      {initialLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : showPaymentForm ? (
        <FeePayment
          fees={selectedQuarterFee}
          onPaymentComplete={handlePaymentComplete}
          onBack={() => {
            setShowPaymentForm(false)
            setSelectedQuarterFee(null)
          }}
          studentId={student?._id}
        />
      ) : (
        <Tabs defaultValue="quarterly" className="w-full">
          <TabsList className="mb-6 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 gap-2 bg-white p-1">
            <TabsTrigger value="quarterly" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white whitespace-nowrap">Current year</TabsTrigger>
            <TabsTrigger value="history" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white whitespace-nowrap">Payment History</TabsTrigger>
          </TabsList>

          <TabsContent value="quarterly">
            <Card>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : quarterlyFees.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {quarterlyFees.map((fee) => {
                      const status = fee.status
                      const isPaid = ["approval", "success", "paid", "pending-approval"].includes(status)
                      const isLive = fee.isLive
                      const amount = fee.totalAmount

                      return (
                        <Card
                          key={fee._id}
                          className={`overflow-hidden ${
                            isPaid
                              ? "bg-green-50"
                              : status === "overdue"
                                ? "bg-red-50"
                                : isLive
                                  ? "bg-blue-50"
                                  : "bg-gray-50"
                          }`}
                        >
                          <CardHeader className="pb-3">
                            <div className="flex justify-between items-center">
                              <CardTitle>{fee.quarter}</CardTitle>
                              <Badge variant={status}>{status}</Badge>
                            </div>
                            <CardDescription className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" /> Due: {formatDate(fee.dueDate)}
                              {(status === "approval" || status === "success" || status === "pending-approval") &&
                                fee.paidDate && (
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-4 w-4" /> Paid: {formatDate(fee.paidDate)}
                                  </div>
                                )}
                            </CardDescription>
                          </CardHeader>
                          <Separator />
                          <CardContent className="pt-4">
                            <div className="flex justify-between items-center mb-4">
                              <span className="text-sm font-medium">{isPaid ? 'Amount Paid:' : 'Fee Amount:'}</span>
                              <span className="text-lg font-bold">
                                {formatCurrency(isPaid ? calculateDisplayTotal(fee) : calculateBaseTotal(fee))}
                              </span>
                            </div>
                            <div className="flex justify-between items-center mb-4">
                              <span className="text-sm font-medium">Status:</span>
                              <span
                                className={`text-sm ${isPaid ? "text-green-600" : isLive ? "text-blue-600" : "text-gray-600"}`}
                              >
                                {status === "pending-approval"
                                  ? "Payment Pending Approval"
                                  : isPaid
                                    ? "Paid on " + formatDate(fee.paidDate)
                                    : isLive
                                      ? "Payment Due"
                                      : "Not Yet Active"}
                              </span>
                            </div>
                            <Button
                              className="w-full mt-2"
                              disabled={isPaid || !isLive}
                              onClick={() => handlePayQuarterlyFee(fee)}
                            >
                              {status === "pending-approval"
                                ? "Payment Pending Approval"
                                : isPaid
                                  ? "Paid"
                                  : isLive
                                    ? `Pay ${formatCurrency(calculateBaseTotal(fee))}`
                                    : "Not Available Yet"}
                            </Button>
                            {isPaid && (
                              <Button
                                onClick={() => handlePrintReceipt(fee)}
                                variant="outline"
                                className="w-full mt-2 flex items-center justify-center"
                              >
                                <Printer className="h-4 w-4 mr-2" /> Print Receipt
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900">No quarterly fees found</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      There are no quarterly fees assigned for the current academic year.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <StudentPaymentHistory
              paymentHistory={paymentHistory}
              formatDate={formatDate}
              formatCurrency={formatCurrency}
              onPrintReceipt={handlePrintReceipt}
              schoolDetails={schoolDetails}
              student={student}
            />
          </TabsContent>
        </Tabs>
      )}

      {/* Fee Receipt Dialog */}
      <Dialog open={isReceiptDialogOpen} onOpenChange={setIsReceiptDialogOpen}>
        <DialogContent className="sm:max-w-md p-0 border-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Fee Receipt</DialogTitle>
          </DialogHeader>
          {selectedFee && (
            <div 
              ref={receiptRef}
              className="relative overflow-hidden"
              style={{
                minHeight: '600px',
                backgroundColor: schoolDetails?.receiptOuterColor || '#ffffff'
              }}
            >
              {/* Center Stamp as Watermark - Behind Text */}
              {schoolDetails?.receiptStamp && (
                <div 
                  className="absolute inset-0 flex justify-center items-center pointer-events-none z-0"
                  style={{
                    opacity: 0.15
                  }}
                >
                  <div className="h-32 w-32 rounded-full overflow-hidden border-2 border-gray-300/30">
                    <img 
                      src={schoolDetails.receiptStamp} 
                      alt="Receipt Stamp" 
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              )}
              
              <div className="relative z-10">
                {/* Outer Layer - Header Section */}
                <div 
                  className="text-center pb-3 pt-3"
                  style={{
                    backgroundColor: schoolDetails?.receiptOuterColor || '#ffffff'
                  }}
                >
                  <div className="px-6">
                    <h3 className="text-base font-bold">{schoolDetails?.name || "School Name"}</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {schoolDetails?.address?.street && `${schoolDetails.address.street}, `}
                      {schoolDetails?.address?.city && `${schoolDetails.address.city}, `}
                      {schoolDetails?.address?.state && `${schoolDetails.address.state} - `}
                      {schoolDetails?.address?.pincode}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {schoolDetails?.phone && `Phone: ${schoolDetails.phone}`}
                      {schoolDetails?.email && ` | Email: ${schoolDetails.email}`}
                    </p>
                  </div>
                </div>

                {/* Inner Layer - Receipt Details Section */}
                <div 
                  className="space-y-3 py-3 px-6 mx-6"
                  style={{
                    backgroundColor: schoolDetails?.receiptInnerColor || '#fef3e2'
                  }}
                >

              <div className="grid grid-cols-2 gap-4 pb-4">
                <div>
                  <p className="text-sm font-medium">Receipt No:</p>
                  <p>REC-{selectedFee?._id ? selectedFee._id.substring(0, 8) : "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Receipt Date:</p>
                  <p>{selectedFee?.paidDate ? formatDate(selectedFee.paidDate) : formatDate(new Date())}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-4">
                {student?.admissionNumber && (
                  <div>
                    <p className="text-sm font-medium">Admission Number:</p>
                    <p>{student.admissionNumber}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium">Quarter:</p>
                  <p>{selectedFee.quarter?.toUpperCase()}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-4">
                <div>
                  <p className="text-sm font-medium">Student Name:</p>
                  <p>{getFullName(student) || "Student Name"}</p>
                </div>
                {student?.fatherName && (
                  <div>
                    <p className="text-sm font-medium">Father's Name:</p>
                    <p>{student.fatherName}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 pb-4">
                {student?.enrollment?.class?.name && (
                  <div>
                    <p className="text-sm font-medium">Class:</p>
                    <p>{student.enrollment.class.name}</p>
                  </div>
                )}
                {student?.enrollment?.section?.name && (
                  <div>
                    <p className="text-sm font-medium">Section:</p>
                    <p>{student.enrollment.section.name}</p>
                  </div>
                )}
              </div>
              
              <div className="border-t border-black mt-3 mb-3"></div>

              <div className="pb-3">
                <h4 className="text-sm font-medium mb-2">Fee Breakdown:</h4>
                <table className="w-full border-collapse text-xs border border-black">
                  <thead>
                    <tr className="border-b border-black">
                      <th className="text-left py-1 px-2 font-medium text-gray-600 border-r border-black">Fee Name</th>
                      <th className="text-right py-1 px-2 font-medium text-gray-600">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedFee.feeBreakup?.map((fee, index) => (
                      <tr key={index} className="border-b border-black">
                        <td className="py-1 px-2 text-gray-600 border-r border-black">{fee.feeName}</td>
                        <td className={`text-right py-1 px-2 font-medium ${fee.amount < 0 ? 'text-green-600' : ''}`}>
                          {fee.amount < 0 ? '-' : ''}₹{Math.abs(fee.amount || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}

                    {(selectedFee?.lateFeeDetails?.lateFeeAmount ?? selectedFee?.lateFees ?? 0) > 0 && !selectedFee.feeBreakup?.some(fee => fee.feeName === "Late Fees") && (
                      <tr className="border-b border-black">
                        <td className="py-1 px-2 text-gray-600 border-r border-black">Late Fee</td>
                        <td className="text-right py-1 px-2 font-medium">₹{Math.abs(selectedFee?.lateFeeDetails?.lateFeeAmount ?? selectedFee?.lateFees ?? 0).toLocaleString()}</td>
                      </tr>
                    )}

                    {selectedFee.scholarshipFees > 0 && !selectedFee.feeBreakup?.some(fee => fee.feeName === "Scholarship Discount") && (
                      <tr className="border-b border-black">
                        <td className="py-1 px-2 text-gray-600 border-r border-black">Scholarship Discount</td>
                        <td className="text-right py-1 px-2 font-medium text-green-600">
                          -₹{Math.abs(selectedFee.scholarshipFees).toLocaleString()}
                        </td>
                      </tr>
                    )}

                    {selectedFee.platformFee > 0 && !selectedFee.feeBreakup?.some(fee => fee.feeName?.includes("Platform Fee")) && (
                      <tr className="border-b border-black">
                        <td className="py-1 px-2 text-gray-600 border-r border-black">Platform Fee (2% + 18% GST)</td>
                        <td className="text-right py-1 px-2 font-medium">₹{Math.abs(selectedFee.platformFee).toLocaleString()}</td>
                      </tr>
                    )}

                    <tr className="border-t border-black">
                      <td className="py-2 px-2 font-semibold text-gray-800 border-r border-black">Total Amount Paid</td>
                      <td className="text-right py-2 px-2 font-bold text-sm text-blue-600">
                        ₹{Math.abs(selectedFee?.totalPayable ?? calculateDisplayTotal(selectedFee) ?? 0).toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

                {/* Payment Method - Inner Layer */}
                <div className="flex justify-between items-start pt-3 pb-3">
                  <div className="flex-1">
                    <p className="text-xs font-medium">Payment Method:</p>
                    <p className="text-xs">{selectedFee.paymentMethod || "Online Payment"}</p>
                  </div>
                </div>
              </div>

                {/* Outer Layer - Signature Section */}
                <div 
                  className="flex justify-end items-start pt-3 pb-3"
                  style={{
                    backgroundColor: schoolDetails?.receiptOuterColor || '#ffffff'
                  }}
                >
                  <div className="text-right flex-shrink-0 px-6">
                    <p className="text-xs font-medium mb-2">Authorized Signature:</p>
                    {schoolDetails?.receiptSignature ? (
                      <div className="mt-2 flex justify-end">
                        <img 
                          src={schoolDetails.receiptSignature} 
                          alt="Authorized Signature" 
                          className="h-24 w-auto object-contain max-w-[200px]"
                        />
                      </div>
                    ) : (
                      <p className="italic mt-4">Principal</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end mt-4 space-x-2 px-6 pb-6">
            <Button onClick={generatePDF} variant="outline">
              <Printer className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            <Button onClick={() => setIsReceiptDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default FeeManagement
